import { message, request as umiRequest } from '@umijs/max';
import { Alert, Button, Spin } from 'antd';
import type { CSSProperties, PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { history } from 'umi';

const API_PREFIX = '/purchasing-access-server';
export const FEISHU_AUTH_LANDING = '/manage';

export type FeishuQRLoginHandle = {
  matchOrigin: (origin: string) => boolean;
  matchData?: (data: unknown) => boolean;
};

export type FeishuUserInfo = {
  name?: string;
  en_name?: string;
  open_id?: string;
  user_id?: string;
  email?: string;
  avatar_url?: string;
};

export type UserRole = 'PURCHASER' | 'COMPLIANCE_EVALUATOR';

export type MeResponse =
  | { loginEnabled?: boolean; loggedIn: false }
  | {
      loginEnabled?: boolean;
      loggedIn: true;
      feishu?: FeishuUserInfo;
      role?: UserRole;
      capabilities?: string[];
    };

type ApiBody<T = unknown> = {
  code?: number;
  data?: T;
  errors?: unknown;
};

type ApiRequestOptions = {
  method?: string;
  data?: unknown;
  silent?: boolean;
};

type GatePhase = 'loading' | 'ready' | 'error' | 'navigating';

export const authVerifyLoadingWrap: CSSProperties = {
  minHeight: '60vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
};

export const authVerifyLoadingText: CSSProperties = {
  margin: 0,
  fontSize: 14,
  color: 'rgba(0, 0, 0, 0.65)',
  lineHeight: 1.5,
  whiteSpace: 'nowrap',
  textAlign: 'center',
};

export const authUserMessages = {
  loadingVerify: '正在验证登录状态，请稍候',
  loadingAuth: '正在完成身份认证，请稍候',
  loadingFeishuClient: '正在通过飞书完成登录，请稍候',

  loginTitle: '供应商准入管理系统',
  loginSubtitleBrowser: '请使用飞书移动端扫描下方二维码登录',
  loginSubtitleFeishu: '正在通过飞书完成登录，请稍候',

  feishuAuthFailedTitle: '身份认证失败',
  feishuAuthFailedDesc:
    '当前无法完成飞书登录，请稍后重试。若问题持续存在，请联系系统管理员。',

  oauthFailed: '飞书授权未完成，请重新扫码登录',
  sdkLoadFailed: '登录组件加载失败，请刷新页面后重试',
  appIdFetchFailed: '系统配置加载失败，请稍后重试或联系管理员',

  sessionExpired: '登录状态已失效，请重新登录',
  networkError: '网络连接异常，请检查后重试',
  requestFailed: '操作未能完成，请稍后重试',

  hintRedirectUri20029:
    '应用重定向地址未在飞书开放平台完成配置，请联系管理员在「安全设置 → 重定向 URL」中登记当前系统访问地址。',
  hintFeishuClientUpgrade: '当前飞书客户端版本较低，请升级至最新版本后重试。',
  hintQrConfig4401:
    '扫码登录参数未通过飞书校验，通常为重定向地址未登记。请联系管理员核对开放平台配置。',
};

export const authLog = {
  debug: (...args: unknown[]) => {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[供应商准入·认证]', ...args);
    }
  },
  info: (...args: unknown[]) => console.info('[供应商准入·认证]', ...args),
  warn: (...args: unknown[]) => console.warn('[供应商准入·认证]', ...args),
  error: (...args: unknown[]) => console.error('[供应商准入·认证]', ...args),
};

export function normalizeMeResponse(raw: unknown): MeResponse {
  if (!raw || typeof raw !== 'object') {
    return { loggedIn: false };
  }
  const o = raw as Record<string, unknown>;
  if (typeof o.loggedIn === 'boolean') {
    return o as MeResponse;
  }
  const inner = o.data;
  if (
    inner &&
    typeof inner === 'object' &&
    typeof (inner as MeResponse).loggedIn === 'boolean'
  ) {
    return inner as MeResponse;
  }
  return { loggedIn: false };
}

export function extractFeishuErr(err: unknown): {
  errno?: number;
  errCode?: number | string;
  errMsg?: string;
  errString?: string;
} {
  if (!err || typeof err !== 'object') return {};
  const o = err as Record<string, unknown>;
  const errno =
    typeof o.errno === 'number'
      ? o.errno
      : typeof o.errno === 'string' && /^\d+$/.test(o.errno)
      ? Number(o.errno)
      : undefined;
  const errCode =
    typeof o.errCode === 'number' || typeof o.errCode === 'string'
      ? (o.errCode as number | string)
      : undefined;
  const errMsg =
    (typeof o.errMsg === 'string' && o.errMsg) ||
    (typeof o.message === 'string' && o.message) ||
    undefined;
  const errString = typeof o.errString === 'string' ? o.errString : undefined;
  return { errno, errCode, errMsg, errString };
}

export function formatAuthErrorForConsole(err: unknown): string {
  if (err == null) return 'unknown';
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.stack || err.message;
  try {
    return JSON.stringify(err, null, 2);
  } catch {
    return String(err);
  }
}

export function resolveFeishuUserHint(err: unknown): string | null {
  const f = extractFeishuErr(err);
  const combined = `${f.errString ?? ''} ${f.errMsg ?? ''}`;
  if (/20029|invalid redirect uri/i.test(combined)) {
    return authUserMessages.hintRedirectUri20029;
  }
  if (f.errno === 103) {
    return authUserMessages.hintFeishuClientUpgrade;
  }
  if (/4401/.test(combined) || f.errCode === 4401 || f.errCode === '4401') {
    return authUserMessages.hintQrConfig4401;
  }
  return null;
}

export async function apiRequest<T>(
  url: string,
  options?: ApiRequestOptions,
): Promise<T> {
  const silent = options?.silent === true;
  try {
    const res = (await umiRequest<ApiBody<T>>(`${API_PREFIX}${url}`, {
      method: options?.method ?? 'GET',
      data: options?.data,
      credentials: 'include',
      getResponse: false,
    })) as ApiBody<T> | T;

    if (res && typeof res === 'object') {
      const body = res as ApiBody<T>;
      if ('code' in body) {
        if (body.code !== 0 && body.code !== undefined) {
          const errMsg =
            typeof body.errors === 'string'
              ? body.errors
              : authUserMessages.requestFailed;
          throw new Error(errMsg);
        }
        if (body.data !== undefined) return body.data as T;
      }
      if ('data' in body && body.data !== undefined && !('loggedIn' in body)) {
        return body.data as T;
      }
    }
    return res as T;
  } catch (err: unknown) {
    const anyErr = err as { response?: { status?: number }; message?: string };
    if (anyErr?.response?.status === 401) {
      authLog.debug('接口返回未登录', url);
      redirectToLoginWhenNeeded();
      throw new Error(authUserMessages.sessionExpired);
    }
    const msg = anyErr?.message || authUserMessages.networkError;
    authLog.warn('请求失败', url, msg, err);
    if (!silent) message.error(msg);
    throw new Error(msg);
  }
}

export const http = {
  get: <T,>(url: string, opts?: Omit<ApiRequestOptions, 'method'>) =>
    apiRequest<T>(url, { ...opts, method: 'GET' }),
  post: <T,>(
    url: string,
    data?: unknown,
    opts?: Omit<ApiRequestOptions, 'method' | 'data'>,
  ) => apiRequest<T>(url, { ...opts, method: 'POST', data }),
};

export const authApi = {
  me: async () =>
    normalizeMeResponse(await http.get<MeResponse>('/auth/me', { silent: true })),
  logout: () => http.post<unknown>('/auth/logout'),
  getAppId: () => http.get<{ appId: string }>('/auth/feishu/appid', { silent: true }),
  feishuCallback: (code: string) =>
    http.get<{ loggedIn: boolean; feishu?: FeishuUserInfo }>(
      `/auth/feishu/callback?code=${encodeURIComponent(code)}`,
      { silent: true },
    ),
};

export function isFeishuInjected(): boolean {
  return Boolean(typeof window !== 'undefined' && window.h5sdk && window.tt);
}

export async function waitForFeishuInjected(maxMs = 4000): Promise<boolean> {
  const started = Date.now();
  while (Date.now() - started < maxMs) {
    if (isFeishuInjected()) return true;
    await new Promise((r) => setTimeout(r, 80));
  }
  return isFeishuInjected();
}

export function safeRedirectParam(): string | null {
  const raw = new URLSearchParams(window.location.search).get('redirect');
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    if (
      !decoded.startsWith('/') ||
      decoded.startsWith('/login') ||
      decoded.startsWith('/form/')
    ) {
      return null;
    }
    return decoded;
  } catch {
    return null;
  }
}

export function isManagePath(pathname: string): boolean {
  return (
    pathname === FEISHU_AUTH_LANDING || pathname.startsWith(`${FEISHU_AUTH_LANDING}/`)
  );
}

export function redirectToLoginWhenNeeded(path = window.location.pathname): void {
  const inFeishu = isFeishuInjected();
  const onManage = isManagePath(path);
  if (!path.startsWith('/form/') && path !== '/login' && !(inFeishu && onManage)) {
    const redirect = encodeURIComponent(path + window.location.search);
    window.location.href = `/login?redirect=${redirect}`;
  }
}

async function exchangeFeishuCode(code: string) {
  return http.get<unknown>(`/auth/feishu/callback?code=${encodeURIComponent(code)}`, {
    silent: true,
  });
}

export function requestFeishuH5Login(appId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    window.h5sdk!.error((err) => reject(err));
    window.h5sdk!.ready(() => {
      authLog.debug('飞书 H5 SDK 就绪，发起 requestAccess');
      window.tt!.requestAccess({
        appID: appId,
        scopeList: [],
        success: async (res) => {
          try {
            await exchangeFeishuCode(res.code);
            authLog.info('飞书免登换票成功');
            resolve();
          } catch (e) {
            reject(e);
          }
        },
        fail: (err) => {
          const { errno } = extractFeishuErr(err);
          authLog.warn('requestAccess 失败', { errno, err });
          if (errno === 103 && typeof window.tt!.requestAuthCode === 'function') {
            authLog.debug('降级使用 requestAuthCode');
            window.tt!.requestAuthCode({
              appId,
              success: async (info) => {
                try {
                  await exchangeFeishuCode(info.code);
                  resolve();
                } catch (e) {
                  reject(e);
                }
              },
              fail: (e2) => reject(e2),
            });
          } else {
            reject(err);
          }
        },
      });
    });
  });
}

export async function runFeishuH5SilentLogin(): Promise<void> {
  const { appId } = await authApi.getAppId();
  authLog.debug('开始飞书 H5 免登', {
    appId: `${appId.slice(0, 6)}...`,
    landing: `${window.location.origin}${FEISHU_AUTH_LANDING}`,
  });
  await requestFeishuH5Login(appId);
  const me = await authApi.me();
  if (!me.loggedIn) throw new Error('飞书免登完成但会话未建立');
}

function isFormPath(pathname: string): boolean {
  return pathname.startsWith('/form/');
}

function isPublicPath(pathname: string): boolean {
  return pathname === '/login' || isFormPath(pathname);
}

function currentRouteKey(): string {
  return `${window.location.pathname}${window.location.search}`;
}

export default function FeishuAuthGate({ children }: PropsWithChildren) {
  const [routeKey, setRouteKey] = useState(currentRouteKey);
  const [phase, setPhase] = useState<GatePhase>('loading');
  const [gateError, setGateError] = useState<unknown>(null);

  useEffect(() => {
    const unlisten = history.listen(() => {
      setRouteKey(currentRouteKey());
    });
    return unlisten;
  }, []);

  useEffect(() => {
    const pathname = window.location.pathname;
    const search = window.location.search;

    if (isFormPath(pathname)) {
      setPhase('ready');
      return;
    }

    let cancelled = false;

    const run = async () => {
      if (isPublicPath(pathname)) {
        authLog.debug('公开路径，跳过飞书免登', pathname);
        if (!cancelled) setPhase('ready');
        return;
      }

      const me0 = await authApi.me();
      if (me0.loggedIn) {
        authLog.debug('会话已有效', pathname);
        const redirectTo = safeRedirectParam();
        if (redirectTo && redirectTo !== pathname) {
          if (!cancelled) setPhase('navigating');
          history.replace(redirectTo);
          return;
        }
        if (!cancelled) setPhase('ready');
        return;
      }

      const injected = await waitForFeishuInjected();
      if (!injected) {
        if (isManagePath(pathname)) {
          if (!cancelled) setPhase('navigating');
          history.replace(`/login?redirect=${encodeURIComponent(pathname + search)}`);
          return;
        }
        if (!cancelled) setPhase('ready');
        return;
      }

      if (isManagePath(pathname) && pathname !== FEISHU_AUTH_LANDING) {
        if (!cancelled) setPhase('navigating');
        const redirect = encodeURIComponent(pathname + search);
        authLog.debug('飞书免登落地页', `${FEISHU_AUTH_LANDING}?redirect=${redirect}`);
        history.replace(`${FEISHU_AUTH_LANDING}?redirect=${redirect}`);
        return;
      }

      if (pathname !== FEISHU_AUTH_LANDING) {
        if (!cancelled) setPhase('ready');
        return;
      }

      try {
        await runFeishuH5SilentLogin();
        const redirectTo = safeRedirectParam();
        if (redirectTo && redirectTo !== pathname) {
          if (!cancelled) setPhase('navigating');
          history.replace(redirectTo);
          return;
        }
        if (!cancelled) setPhase('ready');
      } catch (e) {
        authLog.error('飞书免登流程异常', formatAuthErrorForConsole(e));
        if (!cancelled) {
          setGateError(e);
          setPhase('error');
        }
      }
    };

    setPhase('loading');
    setGateError(null);
    void run();

    return () => {
      cancelled = true;
    };
  }, [routeKey]);

  if (phase === 'loading' || phase === 'navigating') {
    return (
      <div style={authVerifyLoadingWrap}>
        <Spin size="large" />
        <p style={authVerifyLoadingText}>{authUserMessages.loadingVerify}</p>
      </div>
    );
  }

  if (phase === 'error' && gateError) {
    const hint = resolveFeishuUserHint(gateError);
    return (
      <div style={{ maxWidth: 560, margin: '48px auto', padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message={authUserMessages.feishuAuthFailedTitle}
          description={
            <div style={{ marginTop: 8, fontSize: 14, lineHeight: 1.6 }}>
              <div>{authUserMessages.feishuAuthFailedDesc}</div>
              {hint ? <div style={{ marginTop: 10 }}>{hint}</div> : null}
              <div style={{ marginTop: 16 }}>
                <Button type="primary" onClick={() => window.location.reload()}>
                  重新登录
                </Button>
              </div>
            </div>
          }
        />
      </div>
    );
  }

  return <>{children}</>;
}
